import { useState } from "react";
import api from "../lib/api.js";
import { useAsync } from "../lib/hooks.js";
import { useAuth } from "../lib/auth.jsx";
import {
  Badge,
  Callout,
  Card,
  Empty,
  ErrorState,
  Field,
  Loading,
  Modal,
  PageIntro,
  formatDateTime,
  useToast,
} from "../components/ui.jsx";
import { IconPlus, IconRefresh } from "../components/icons.jsx";
import { ResetPassword } from "../components/ChangePassword.jsx";

const ROLES = [
  {
    value: "SYSTEM_ADMIN",
    label: "System administrator",
    detail: "Manages tenants, operators and every device.",
  },
  {
    value: "OPERATOR",
    label: "Operator",
    detail: "Manages devices and sends commands, but not tenants or operators.",
  },
  { value: "READ_ONLY", label: "Read only", detail: "Can look at everything and change nothing." },
];

export default function Operators() {
  const toast = useToast();
  const { session } = useAuth();
  const { data, error, loading, reload } = useAsync(() => api.listOperators(), []);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(null);

  const operators = data ?? [];

  async function toggle(op) {
    try {
      await api.setOperatorActive(op.email, !op.active);
      reload();
      toast.success(
        op.active ? "Account disabled" : "Account enabled",
        op.active ? "Their live sessions were revoked immediately." : undefined
      );
    } catch (err) {
      toast.error("Could not change the account", err.message);
    }
  }

  return (
    <>
      <PageIntro title="Platform staff">
        Every privileged action is recorded against the person who performed it, so each
        member of staff needs their own account. Disabling an account revokes its live
        sessions at once rather than waiting for them to expire.
      </PageIntro>

      <div className="toolbar">
        <span className="spacer" />
        <button className="btn btn-secondary btn-sm" onClick={reload}>
          <IconRefresh /> Refresh
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
          <IconPlus /> New operator
        </button>
      </div>

      {loading ? (
        <Loading label="Loading operators" />
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : operators.length === 0 ? (
        <Card>
          <Empty title="No operators">Create an account for each member of staff.</Empty>
        </Card>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Email address</th>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th className="cell-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {operators.map((op) => (
                <tr key={op.email}>
                  <td className="mono">{op.email}</td>
                  <td>{op.display_name || <span className="hint">Not set</span>}</td>
                  <td>{ROLES.find((r) => r.value === op.role)?.label ?? op.role}</td>
                  <td>
                    <Badge kind={op.active ? "success" : "neutral"}>
                      {op.active ? "Active" : "Disabled"}
                    </Badge>
                  </td>
                  <td className="hint">{formatDateTime(op.created_at)}</td>
                  <td className="cell-actions">
                    {op.email === session?.email ? (
                      <span className="hint">This is you</span>
                    ) : (
                      <>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setResetting(op.email)}
                        >
                          Reset password
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => toggle(op)}>
                          {op.active ? "Disable" : "Enable"}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateOperator
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            reload();
            toast.success("Operator created", "They can sign in with the password you set.");
          }}
        />
      )}

      {resetting && (
        <ResetPassword email={resetting} onClose={() => setResetting(null)} />
      )}
    </>
  );
}

function CreateOperator({ onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "",
    password: "",
    display_name: "",
    role: "OPERATOR",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const passwordTooShort = form.password.length > 0 && form.password.length < 12;
  const role = ROLES.find((r) => r.value === form.role);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createOperator(form);
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Create an operator"
      hint="Give the password to them over a channel you trust"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            form="create-operator"
            disabled={busy || !form.email || form.password.length < 12}
          >
            {busy ? <span className="spinner" /> : null}
            Create operator
          </button>
        </>
      }
    >
      <form id="create-operator" onSubmit={submit}>
        {error && <Callout kind="warning">{error}</Callout>}

        <Field
          label="Email address"
          required
          hint="This is how they sign in, and where their sign-in code is sent. It has to be a mailbox they can actually read."
        >
          <input
            className="input mono"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })}
            required
          />
        </Field>

        <Field label="Full name">
          <input
            className="input"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          />
        </Field>

        <Field
          label="Initial password"
          required
          error={passwordTooShort ? "At least twelve characters." : null}
          hint="Twelve characters minimum. Five failed sign-ins lock the account for fifteen minutes."
        >
          <input
            className="input"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
          />
        </Field>

        <Field label="Role" required hint={role?.detail}>
          <select
            className="select"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </Field>

        {form.role === "SYSTEM_ADMIN" && (
          <Callout kind="warning">
            A system administrator can create and disable other operators, and can change
            tenants. Grant it only where it is genuinely needed.
          </Callout>
        )}
      </form>
    </Modal>
  );
}
